import { useState, useEffect, useCallback } from 'react';
import {
  getUpcomingWorkshopTiming,
  getWorkshopCountdown,
  shouldUpdateSchedule,
  getMotivationalMessage,
  cacheWorkshopTiming,
  getCachedWorkshopTiming,
  formatWorkshopTiming
} from '../utils/workshopScheduler';

/**
 * Custom hook for managing workshop timing and auto-updates
 * @returns {Object} Workshop timing data and utilities
 */
export function useWorkshopTiming() {
  const [workshopTiming, setWorkshopTiming] = useState(null);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [motivationalMessage, setMotivationalMessage] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Update workshop timing data
   */
  const updateWorkshopTiming = useCallback(() => {
    try {
      const timing = getUpcomingWorkshopTiming();
      const formattedTiming = formatWorkshopTiming(timing);
      const message = getMotivationalMessage();
      
      setWorkshopTiming({ ...timing, formatted: formattedTiming });
      setMotivationalMessage(message);
      setLastUpdated(new Date());
      
      // Cache the timing data
      cacheWorkshopTiming({ ...timing, formatted: formattedTiming });
      
      // Track timing update
      if (typeof window.dataLayer !== 'undefined') {
        window.dataLayer.push({
          'event': 'workshop_timing_updated',
          'event_category': 'system',
          'event_label': 'Auto Workshop Timing Update',
          'workshop_date': timing.dateString,
          'days_until': timing.daysUntil
        });
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Failed to update workshop timing:', error);
      setIsLoading(false);
    }
  }, []);

  /**
   * Update countdown timer
   */
  const updateCountdown = useCallback(() => {
    const countdownData = getWorkshopCountdown();
    setCountdown(countdownData);
    
    // If workshop is expired, update timing for next week
    if (countdownData.isExpired && workshopTiming) {
      updateWorkshopTiming();
    }
  }, [workshopTiming, updateWorkshopTiming]);

  /**
   * Check for automatic updates (every Sunday at 12:00 AM)
   */
  const checkForAutoUpdate = useCallback(() => {
    if (shouldUpdateSchedule()) {
      console.log('🕛 Sunday 12:00 AM - Auto-updating workshop timing...');
      updateWorkshopTiming();
      
      // Track auto-update event
      if (typeof window.dataLayer !== 'undefined') {
        window.dataLayer.push({
          'event': 'auto_update_triggered',
          'event_category': 'system',
          'event_label': 'Sunday Midnight Auto Update'
        });
      }
    }
  }, [updateWorkshopTiming]);

  // Initialize workshop timing
  useEffect(() => {
    // Try to get cached data first
    const cached = getCachedWorkshopTiming();
    if (cached) {
      setWorkshopTiming(cached);
      setMotivationalMessage(getMotivationalMessage());
      setIsLoading(false);
    } else {
      updateWorkshopTiming();
    }
  }, [updateWorkshopTiming]);

  // Set up countdown timer (updates every second)
  useEffect(() => {
    const countdownInterval = setInterval(updateCountdown, 1000);
    return () => clearInterval(countdownInterval);
  }, [updateCountdown]);

  // Set up auto-update checker (checks every minute)
  useEffect(() => {
    const autoUpdateInterval = setInterval(checkForAutoUpdate, 60000); // Check every minute
    return () => clearInterval(autoUpdateInterval);
  }, [checkForAutoUpdate]);

  // Set up daily check at midnight
  useEffect(() => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(now.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    
    const msUntilMidnight = tomorrow.getTime() - now.getTime();
    
    const midnightTimeout = setTimeout(() => {
      checkForAutoUpdate();
      
      // Set up daily interval after first midnight check
      const dailyInterval = setInterval(checkForAutoUpdate, 24 * 60 * 60 * 1000);
      
      return () => clearInterval(dailyInterval);
    }, msUntilMidnight);
    
    return () => clearTimeout(midnightTimeout);
  }, [checkForAutoUpdate]);

  /**
   * Manual refresh function
   */
  const refreshTiming = useCallback(() => {
    setIsLoading(true);
    updateWorkshopTiming();
  }, [updateWorkshopTiming]);

  /**
   * Get formatted timing for specific contexts
   */
  const getFormattedTiming = useCallback((context = 'heroSection') => {
    if (!workshopTiming?.formatted) return null;
    return workshopTiming.formatted[context] || workshopTiming.formatted.heroSection;
  }, [workshopTiming]);

  /**
   * Check if workshop is happening today
   */
  const isWorkshopToday = workshopTiming?.isToday || false;

  /**
   * Check if workshop is happening tomorrow
   */
  const isWorkshopTomorrow = workshopTiming?.daysUntil === 1;

  /**
   * Get urgency level based on days until workshop
   */
  const getUrgencyLevel = useCallback(() => {
    if (!workshopTiming) return 'normal';
    
    const days = workshopTiming.daysUntil;
    if (days === 0) return 'today';
    if (days === 1) return 'tomorrow';
    if (days <= 3) return 'urgent';
    if (days <= 7) return 'soon';
    return 'normal';
  }, [workshopTiming]);

  return {
    // Core data
    workshopTiming,
    countdown,
    motivationalMessage,
    lastUpdated,
    isLoading,
    
    // Status checks
    isWorkshopToday,
    isWorkshopTomorrow,
    urgencyLevel: getUrgencyLevel(),
    
    // Utilities
    refreshTiming,
    getFormattedTiming,
    
    // Formatted data for easy access
    displayDate: workshopTiming?.formattedForDisplay?.short || 'Loading...',
    displayDateTamil: workshopTiming?.formattedForDisplay?.tamil || 'Loading...',
    nextBatchText: workshopTiming?.formatted?.heroSection?.countdown || 'Loading...'
  };
}

export default useWorkshopTiming;